import { SearchIcon, Trash2Icon } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";

interface SearchCondition {
  fieldId: string;
  operator: string;
  value: string;
  joinOperator: "و" | "یا" | "بجز";
}

interface AdvancedSearchModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  fields: Array<{ id: string; name: string }>;
  onSearch: (query: string) => void;
}

export const PageAdvancedSearchModal: React.FC<AdvancedSearchModalProps> = ({
  isOpen,
  onOpenChange,
  fields,
  onSearch
}) => {
  const [conditions, setConditions] = useState<SearchCondition[]>([
    { fieldId: "", operator: "contains", value: "", joinOperator: "و" }
  ]);

  const operators = [
    { value: "contains", label: "شامل (contains)" },
    { value: "equals", label: "دقیقا برابر (equals)" },
    { value: "startsWith", label: "شروع با (startsWith)" },
    { value: "endsWith", label: "پایان با (endsWith)" }
  ];

  const mapJoinOperatorToQuery = (
    joinOperator: SearchCondition["joinOperator"]
  ) => {
    switch (joinOperator) {
      case "و":
        return "AND";
      case "یا":
        return "OR";
      case "بجز":
        return "EXCEPT";
      default:
        return "AND";
    }
  };

  useEffect(() => {
    if (!fields.length) return;

    setConditions((prev) =>
      prev.map((condition) =>
        condition.fieldId ? condition : { ...condition, fieldId: fields[0].id }
      )
    );
  }, [fields]);

  const addCondition = () => {
    setConditions([
      ...conditions,
      { fieldId: "", operator: "contains", value: "", joinOperator: "و" }
    ]);
  };

  const removeCondition = (index: number) => {
    if (conditions.length > 1) {
      setConditions(conditions.filter((_, i) => i !== index));
    }
  };

  const updateCondition = (
    index: number,
    field: keyof SearchCondition,
    val: string
  ) => {
    const updated = [...conditions];
    (updated[index] as any)[field] = val;
    setConditions(updated);
  };

  const generateQuery = (): string => {
    const validConditions = conditions.filter(
      (cond) => cond.fieldId && cond.value
    );
    const parts: string[] = [];

    validConditions.forEach((cond, index) => {
      if (index > 0) {
        parts.push(` ${mapJoinOperatorToQuery(cond.joinOperator)} `);
      }

      parts.push(`${cond.fieldId}:${cond.operator}:"${cond.value}"`);
    });

    return parts.join("");
  };

  const handleSearch = () => {
    const query = generateQuery();
    if (query) {
      onSearch(query);
      onOpenChange(false);
    }
  };

  const handleClear = () => {
    setConditions([
      { fieldId: "", operator: "contains", value: "", joinOperator: "و" }
    ]);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <SearchIcon className="h-5 w-5" />
            جستجوی پیشرفته
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {conditions.map((condition, index) => (
            <div key={index} className="flex items-end gap-2">
              {index > 0 && (
                <Select
                  value={condition.joinOperator}
                  onValueChange={(val: "و" | "یا" | "بجز") =>
                    updateCondition(index, "joinOperator", val)
                  }
                >
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="و">و</SelectItem>
                    <SelectItem value="یا">یا</SelectItem>
                    <SelectItem value="بجز">بجز</SelectItem>
                  </SelectContent>
                </Select>
              )}

              <Select
                value={condition.fieldId}
                onValueChange={(val) => updateCondition(index, "fieldId", val)}
              >
                <SelectTrigger
                  className={index === 0 ? "flex-[1.45]" : "flex-1"}
                >
                  <SelectValue placeholder="انتخاب فیلد" />
                </SelectTrigger>
                <SelectContent>
                  {fields.map((field) => (
                    <SelectItem key={field.id} value={field.id}>
                      {field.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={condition.operator}
                onValueChange={(val) => updateCondition(index, "operator", val)}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {operators.map((op) => (
                    <SelectItem key={op.value} value={op.value}>
                      {op.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Input
                type="text"
                placeholder="مقدار جستجو"
                value={condition.value}
                onChange={(e) =>
                  updateCondition(index, "value", e.target.value)
                }
                className="flex-1"
              />

              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeCondition(index)}
                disabled={conditions.length === 1}
              >
                <Trash2Icon className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>

        <Button variant="outline" onClick={addCondition} className="w-full">
          + شرط جدید
        </Button>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={handleClear}>
            پاک کردن
          </Button>
          <Button style={{ backgroundColor: "red" , color : "white" }} variant="outline" onClick={() => onOpenChange(false)}>
            انصراف
          </Button>
          <Button
            onClick={handleSearch}
            style={{ backgroundColor: "hsl(var(--primary))", color: "white" }}
          >
            جستجو
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
